import React from 'react';

import { Reveal } from '../Reveal/Reveal';
import { labelClampPx, pillCentrePct } from './pill-centre';
import { Skeleton } from '../Skeleton/Skeleton';
import { Text } from '../Text/Text';
import {
  Dot,
  Label,
  Marker,
  MarkerArrow,
  MarkerLabel,
  MarkerLabelBox,
  MarketFill,
  Root,
  PillGap,
  PillSlot,
  TierPill,
  Track,
  TrackArea,
  MARKER_CAP_PX,
  YouFill,
  type MarkerAlign,
} from './VsMarketBar.styles';

export type VsMarketBarProps = {
  /** 0-100. Filled portion of the track — where "You" stand. 0 draws a round dot. */
  value: number;
  /** 0-100. Where the market-average marker sits under the track. */
  market: number;
  /** Text on the pill riding the fill's tip: the tier the feature runs on today, or "Off" when it is
   *  not enabled (`value` 0) — in which case the pill takes the zero-dot's place at the track's
   *  start and is painted in the inactive red. Omit for no pill. */
  tierLabel?: string;
  /** When false the fill stays empty (pre-mount state). */
  animate: boolean;
  /** Stagger offset in ms so a column of bars fills in sequence. */
  delay?: number;
  /** While true, the track shows a sheen placeholder and the markers row stays
   * reserved-but-empty (no layout shift) until the data loads. */
  loading?: boolean;
  className?: string;
};

/** You and Market sit on the same spot — show one combined marker.
 *  This is a label-width margin, not just a point-of-contact one: the only real consumer,
 *  FeatureCard's BarSlot, is as narrow as 180px (`min-width: 180px`, FeatureCard.styles.ts), and at
 *  that width the "You"/"Market" arrow+label columns visually collide well before their raw % diff
 *  reaches 0. Measured at 180px: diff=2 (the old threshold) still collided (labels touching from
 *  ~diff 4 up to ~19); diff=20 is the first value with headroom to spare across that whole range. */
const COMBINE_WITHIN = 20;

/** A marker's rendered anchor is held a cap's width in from each track end (`markerLeft` in
 *  VsMarketBar.styles.ts), so the merge check compares these CLAMPED positions, not the raw
 *  value/market: e.g. value=0 and market=3 both land on that same floor and would render their
 *  arrows on the exact same spot (labels colliding) despite a raw diff above COMBINE_WITHIN. */
const clampMarker = (n: number): number => Math.min(96, Math.max(4, n));

/** Keep an edge marker's label from being clipped by the bar's edge: near the left the column runs
 *  right, near the right it runs left, otherwise it centres over its arrow. */
const alignFor = (left: number): MarkerAlign => {
  if (left <= 10) return 'start';
  if (left >= 90) return 'end';
  return 'center';
};

const ArrowUp = (): React.ReactElement => (
  <MarkerArrow viewBox="0 0 12 8" aria-hidden="true">
    <path d="M6 1.5L10.5 6.5L1.5 6.5Z" />
  </MarkerArrow>
);

/**
 * "You vs Market" comparison bar in the find-my-pack language (the configurator's detailed
 * feature view, which pages/product-detail's BenchmarkBar also follows): a hatched market range
 * (0 → `market`%), a deep-blue→periwinkle "You" fill that grows to `value`% on mount, a pill riding
 * the fill's tip naming the tier it stands on, and You/Market arrow markers underneath (merged into
 * "You and Market" when they coincide). At `value` 0 there is no fill, so the pill itself marks the
 * spot, in the inactive red, stating the feature is "Off" — what the owner HAS, never what
 * activating would unlock. The fill colour is uniform across features (status is carried by the
 * card's status line). The "You vs Market" label is static chrome; bar + markers load in.
 */
export const VsMarketBar = ({
  value,
  market,
  tierLabel,
  animate,
  delay = 0,
  loading = false,
  className,
}: VsMarketBarProps): React.ReactElement => {
  const width = animate ? value : 0;
  const marketW = animate ? market : 0;

  // The "You" marker is placed under the PILL's centre, not under the raw value: the two state the
  // same thing, so they have to line up. The offset is only knowable from real measurements (a
  // px-sized label against a percentage track), so both boxes are observed and the arithmetic lives
  // in `pillCentrePct`. Until the first measurement lands the marker stays on the value.
  const trackRef = React.useRef<HTMLDivElement>(null);
  const pillRef = React.useRef<HTMLSpanElement>(null);
  const labelRef = React.useRef<HTMLSpanElement>(null);
  const marketLabelRef = React.useRef<HTMLSpanElement>(null);
  const [boxes, setBoxes] = React.useState<{
    pill: number;
    track: number;
    label: number;
    marketLabel: number;
  }>({ pill: 0, track: 0, label: 0, marketLabel: 0 });

  const centred = tierLabel ? pillCentrePct(width, boxes.pill, boxes.track) : null;
  const youAt = centred ?? width;
  // Merge on where the markers actually LAND, not on the raw values: with the You marker moved onto
  // the pill's centre (up to half a pill left of its value), the two labels could touch at a raw
  // difference the threshold still called safe — measured at 0.12px apart on a 180px card.
  //
  // Read from the FINAL value, never the animating one (same rule as `marketOnTop` below): the
  // merged and split markers are different elements, so a mid-entrance flip unmounts the marker
  // and remounts it, its `transition: left` never starts, and the arrow jumps instead of sliding —
  // measured at 483px away from the pill it is anchored to, one frame in.
  const settledYouAt = (tierLabel ? pillCentrePct(value, boxes.pill, boxes.track) : null) ?? value;
  const combined = Math.abs(clampMarker(settledYouAt) - clampMarker(market)) <= COMBINE_WITHIN;
  // The arrow stays where it marks; a label that would hang off the bar slides instead. Applied on
  // the centred placements only — an edge-aligned column already runs its label inward, and this
  // clamp reasons about a label centred on its arrow, so feeding it one that is not would shift a
  // correct label off. On the non-pill path the arrow sits a cap's width in from its raw position
  // (markerShift), which is what makes the difference between 0 and a visible overhang here.
  const youAlign = alignFor(youAt);
  // On the non-pill path the arrow sits a cap's width in from its raw position (markerShift); on the
  // pill path it sits on the pill's centre, with no cap to nudge towards.
  const arrowPx = centred == null ? (youAt / 100) * boxes.track - MARKER_CAP_PX : (youAt / 100) * boxes.track;
  const labelShift =
    centred != null || youAlign === 'center' ? labelClampPx(arrowPx, boxes.label, boxes.track) : 0;
  // The Market marker is placed the same way and its label is just as wide, so it needs the same
  // clamp: centred at a low market value on a 180px card it hung 9px off the track, at rest.
  const marketAlign = alignFor(market);
  const marketShift =
    marketAlign === 'center'
      ? labelClampPx((market / 100) * boxes.track - MARKER_CAP_PX, boxes.marketLabel, boxes.track)
      : 0;

  React.useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) {
      setBoxes((prev) =>
        prev.pill === 0 && prev.track === 0 && prev.label === 0 && prev.marketLabel === 0
          ? prev
          : { pill: 0, track: 0, label: 0, marketLabel: 0 },
      );
      return;
    }
    // The pill is OPTIONAL (a bar with no tier name). Bailing when it is missing skipped the very
    // state whose label has nothing to clamp it — the marker is not pill-anchored there, so it is
    // the centred path that has to hold the label on the track.
    const pill = pillRef.current;
    const read = (): void => {
      // WIDTHS only, and none of them animate — only positions do — so these stay valid through the
      // entrance and the marker slides on CSS alone.
      const next = {
        pill: pill?.getBoundingClientRect().width ?? 0,
        track: track.getBoundingClientRect().width,
        label: labelRef.current?.getBoundingClientRect().width ?? 0,
        marketLabel: marketLabelRef.current?.getBoundingClientRect().width ?? 0,
      };
      setBoxes((prev) =>
        prev.pill === next.pill &&
        prev.track === next.track &&
        prev.label === next.label &&
        prev.marketLabel === next.marketLabel
          ? prev
          : next,
      );
    };
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(read);
    observer.observe(track);
    if (pill) observer.observe(pill);
    if (labelRef.current) observer.observe(labelRef.current);
    if (marketLabelRef.current) observer.observe(marketLabelRef.current);
    return () => observer.disconnect();
  }, [tierLabel, loading, combined]);
  // Layering decided from the FINAL values (not the animating widths) so the market hatch's z-order
  // stays stable through the mount grow instead of flipping mid-animation.
  const marketOnTop = value >= market;

  return (
    <Root className={className}>
      <Label text="You vs Market" tag="span" />
      <TrackArea>
        <Track ref={trackRef}>
          <Reveal overlay loading={loading} placeholder={<Skeleton block width="100%" height="100%" radius={12} />}>
            {market > 0 ? <MarketFill $w={marketW} $delay={delay} $onTop={marketOnTop} aria-hidden="true" /> : null}
            {value > 0 ? <YouFill $w={width} $delay={delay} aria-hidden="true" /> : null}
            {/* At 0 the pill IS the "You" marker on the track (it says "Off"); the dot only stands
                in when there is no pill to state it. */}
            {value <= 0 && !tierLabel ? <Dot aria-hidden="true" /> : null}
            {/* The slot reads the ANIMATED width, not the final value: reading the final one rode a
                tip that was still at 0, putting the whole pill to the LEFT of the track and sweeping
                it across the card's name column for the length of the entrance (nothing here clips
                it). It carries the fill's own delay too, so a staggered card's pill rides its fill
                instead of leading it. */}
            {tierLabel ? (
              <PillSlot>
                <TierPill ref={pillRef} $off={value <= 0}>
                  <Text text={tierLabel} tag="span" />
                </TierPill>
                <PillGap $at={width} $delay={delay} aria-hidden="true" />
              </PillSlot>
            ) : null}
          </Reveal>
        </Track>
        {loading ? null : combined ? (
          <Marker $left={youAt} $align={youAlign} $onPill={centred != null} $delay={delay}>
            <ArrowUp />
            <MarkerLabelBox ref={labelRef} $shift={labelShift}>
              <MarkerLabel text="You and Market" tag="span" />
            </MarkerLabelBox>
          </Marker>
        ) : (
          <>
            <Marker $left={youAt} $align={youAlign} $onPill={centred != null} $delay={delay}>
              <ArrowUp />
              <MarkerLabelBox ref={labelRef} $shift={labelShift}>
                <MarkerLabel text="You" tag="span" />
              </MarkerLabelBox>
            </Marker>
            <Marker $left={market} $align={marketAlign} $delay={0}>
              <ArrowUp />
              <MarkerLabelBox ref={marketLabelRef} $shift={marketShift}>
                <MarkerLabel text="Market" tag="span" />
              </MarkerLabelBox>
            </Marker>
          </>
        )}
      </TrackArea>
    </Root>
  );
};

export default VsMarketBar;
